import React, { useEffect, useState } from 'react'
import { css } from '@emotion/core'
import { Controller, UseFormReturn } from 'react-hook-form'
import { Select, Option } from '@/components/atoms/select'
import { useTranslation } from 'react-i18next'
import { ApprovedStatusType, approvedStatusValues } from '@/models/reservation-approved-status'
import { timesBy15Minute } from '@/libs/times'
import * as api from '@/apis/aipass'
import {
  DateExecution,
  MailDeliveryContentInput,
  MailDeliveryStayTypeTarget,
  dateExecution,
  lineNotificationStatus,
  stayTypeTarget,
  targetStatus,
} from '@/models/mail-delivery/mail-delivery-content'
import { ChannelCodeType } from '@/models/reservation-channel-code'
import { Checkbox } from '@/components/atoms/checkbox'
import { Hint } from '@/components/atoms/hint'
import { RadioGroupField } from '@/components/molecules/radio-group-field'

interface DetailContentFormProps {
  useFormReturn: UseFormReturn<MailDeliveryContentInput>
}

export const defaultValues = {
  deliveryDate: { dateExecution: DateExecution.Ago },
  reservationStatuses: [ApprovedStatusType.Reservation, ApprovedStatusType.Checkin, ApprovedStatusType.Stay, ApprovedStatusType.Checkout],
  stayTypeTarget: 'all' as const,
}

export const FormDeliveryCondition: React.FC<DetailContentFormProps> = ({ useFormReturn }) => {
  const [reservationChannelCodes, setReservationChannelCodes] = useState<ChannelCodeType[]>([])
  const { t } = useTranslation()
  const { control, register, setValue, getValues } = useFormReturn

  useEffect(() => {
    api.fetchReservationChannelCode().then(res => {
      setReservationChannelCodes(res)
      if (getValues('channelCodes') === undefined) {
        setValue('channelCodes', ['-', ...res.map(({ id }) => id)])
      }
    })
  }, [])

  return (
    <div css={inputBlockStyle}>
      <div className="input-divide">
        <p className="head-label date">
          {t('Scheduled delivery date')}
          <span css={requireLabelTextStyle}>※</span>
        </p>
        <div className="delivery-date">
          <select {...register('deliveryDate.targetStatus')} style={{ width: 174 }}>
            {targetStatus.map(({ value, label }) => (
              <option key={value} value={value}>
                {t(label)}
              </option>
            ))}
          </select>

          <input
            {...register('deliveryDate.numberOfDay', { setValueAs: v => parseInt(v) })}
            type="number"
            style={{ width: 58 }}
            min={0}
            placeholder="1"
          />

          <select {...register('deliveryDate.dateExecution')} style={{ width: 84 }}>
            {dateExecution.map(({ value, label }) => (
              <option key={value} value={value}>
                {t(label)}
              </option>
            ))}
          </select>

          <select
            {...register('deliveryDate.deliveryTime')}
            style={{ width: 129 }}
            onFocus={() => {
              const deliveryTime = getValues('deliveryDate.deliveryTime')
              if (!deliveryTime) {
                setValue('deliveryDate.deliveryTime', '12:00')
              }
            }}
          >
            <option value={''}>{t('Select a time')}</option>
            {timesBy15Minute.map(time => (
              <option key={time.value} value={time.value}>
                {time.name}
              </option>
            ))}
          </select>
        </div>
        <div className="send-to-after-delivery">
          <Controller
            control={control}
            name="isSendToAfterDelivery"
            render={({ field: { onChange, value } }) => {
              return (
                <Checkbox
                  value={value}
                  onChange={onChange}
                  label={t(
                    'Delivery will be made to reservations that are eligible for delivery after the scheduled delivery date and time.',
                  )}
                />
              )
            }}
          />
          <Hint
            customCss={css({
              paddingLeft: 8,
              height: 18,
              position: 'static',
              '.help-icon': { width: 18, height: 18 },
              '.help-popup': {
                left: 'calc(18px + 16px)',
                top: -18,
                position: 'relative',
              },
            })}
          >
            <div className="-popup">
              <div>{t('SendToAfterDeliveryPopupLine1')}</div>
              <div style={{ paddingTop: 11.5 }}>({t('e.g.')})</div>
              <div>{t('SendToAfterDeliveryPopupLine2')}</div>
              <div>{t('SendToAfterDeliveryPopupLine3')}</div>
            </div>
          </Hint>
        </div>

        <div className="delivery-target input-divide">
          <p className="head-label target">{t('delivery target')}</p>
          <div className="input-space" style={{ width: 715 }}>
            <label>{t('mailDelivery.stayTypeTarget')}</label>
            <Controller
              control={control}
              name="stayTypeTarget"
              render={({ field }) => (
                <RadioGroupField
                  value={field.value}
                  onChange={e => field.onChange(e.target.value as MailDeliveryStayTypeTarget)}
                  items={stayTypeTarget.map(({ label, value }) => ({
                    value,
                    label,
                  }))}
                  style={{ direction: 'row', radioSize: 18, labelMargin: 8 }}
                  itemCss={css({
                    marginRight: 24,
                    '> span:last-child': {
                      whiteSpace: 'nowrap',
                    },
                  })}
                />
              )}
            />
          </div>

          <div className="input-space" style={{ width: 415 }}>
            <label>{t('Accommodation management status')}</label>
            <Controller
              control={control}
              name="reservationStatuses"
              render={({ field }) => {
                return (
                  <Select
                    mode="multiple"
                    className="delivery-select"
                    removeIcon={<img src={require('@/static/images/mail-delivery/form_delete.svg')} />}
                    placeholder={t('Please select')}
                    onChange={field.onChange}
                    value={field.value}
                  >
                    {Object.keys(approvedStatusValues)
                      .filter(key => key !== ApprovedStatusType.Customer.toString())
                      .map(key => (
                        <Option key={key} value={Number(key)}>
                          {approvedStatusValues[key]}
                        </Option>
                      ))}
                  </Select>
                )
              }}
            />
          </div>

          <div className="input-space" style={{ width: 715 }}>
            <label>{t('Channel code name')}</label>
            <Controller
              control={control}
              name="channelCodes"
              render={({ field }) => (
                <Select
                  mode="multiple"
                  className="delivery-select"
                  removeIcon={<img src={require('@/static/images/mail-delivery/form_delete.svg')} />}
                  placeholder={t('Please select')}
                  showSearch={false}
                  onChange={field.onChange}
                  value={field.value}
                >
                  {[{ id: '-', name: '-' }, ...reservationChannelCodes].map(reservationChannelCode => (
                    <Option key={reservationChannelCode.id} value={reservationChannelCode.id!}>
                      {reservationChannelCode.name}
                    </Option>
                  ))}
                </Select>
              )}
            />
          </div>

          <div className="input-space">
            <label>{t('mailDelivery.LINE')}</label>
            <select {...register('lineNotificationStatus')} style={{ width: 97 }}>
              {lineNotificationStatus.map(({ label, value }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  )
}

const inputBlockStyle = css({
  padding: '32px',
  '.input-space > label': {
    display: 'block',
    marginBottom: 12,
    fontSize: 12,
    fontWeight: 'bold',
  },
  input: {
    height: 32,
    padding: '8px 16px',
    border: '1px solid #CCCCCC',
    borderRadius: 17,
    textAlign: 'left',
    '::placeholder': {
      color: '#CCCCCC',
    },
  },
  select: {
    width: '100%',
    height: 32,
    fontSize: 14,
    borderRadius: 19,
    padding: '0 24px 0 16px',
    appearance: 'none',
    backgroundColor: '#fff',
    backgroundImage: `url(${require('@/static/images/select.svg')})`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: '90% center',
    backgroundSize: 10,
    borderColor: '#CCCCCC',
  },
  '.delivery-select': {
    li: {
      fontSize: 12,
      fontWeight: 'bold',
    },
    span: {
      right: 10,
    },
  },
  '.editor-content': {
    paddingBottom: 32,
  },
  '.head-label': {
    fontSize: 14,
    fontWeight: 'bold',
    '&.date': {
      display: 'flex',
      gap: 8,
      marginBottom: 12,
    },
    '&.target': {
      marginBottom: 21,
    },
  },
  '.delivery-date': {
    display: 'flex',
    gap: 16,
  },
  '.send-to-after-delivery': {
    marginTop: 16,
    marginBottom: 32,
    display: 'flex',
    '.-popup': {
      lineHeight: '21px',
      margin: '-4.5px 0',
      fontSize: 12,
      fontWeight: 'bold',
      width: 386,
      padding: 16,
      '> div': {
        letterSpacing: 1.2,
      },
    },
  },
  '.input-divide': {
    paddingInline: 32,
    paddingTop: 30,
    marginInline: -32,
    borderTop: '1px solid #F2F2F2',
  },
  '.input-space': {
    marginTop: 24,
  },
})

const requireLabelTextStyle = css({
  fontSize: 10,
  fontWeight: 'bold',
  color: '#676767',
})
